import { Link, useNavigate } from "react-router-dom";
import {
  Search,
  Settings,
  LogOut,
  User,
  ChevronDown,
  AlertTriangle,
  Menu,
  ShieldAlert,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { UserAvatar } from "@/components/ui/user-avatar";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuthStore } from "@/store/authStore";
import { useStopImpersonate } from "@/features/auth/useStopImpersonate";
import { isMfaDeferred } from "@/lib/auth/jwt";
import { useRoleCatalog } from "@/hooks/useRoleCatalog";
import { NotificationBell } from "./NotificationBell";
import type { NavDrawerState } from "./useNavDrawer";

export function Header({
  nav,
}: {
  /**
   * The navigation drawer this header's menu button opens, below `md`. Left
   * out, there is no menu button.
   */
  nav?: NavDrawerState;
} = {}) {
  const { user, impersonation, logout, accessToken } = useAuthStore();
  const mfaDeferred = accessToken ? isMfaDeferred(accessToken) : false;
  const navigate = useNavigate();
  const { stop: stopImpersonating, isPending: stopImpersonatingPending } =
    useStopImpersonate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Role labels come from the server's catalog; `labelOf` humanises the code
  // while it loads.
  const { labelOf } = useRoleCatalog();

  return (
    <div className="relative z-40 shrink-0">
      {/* Impersonation Banner */}
      {impersonation.isImpersonating && (
        <div className="flex h-9 items-center justify-center gap-2 bg-warning/10 text-warning text-xs font-medium border-b border-warning/20">
          <AlertTriangle className="h-3.5 w-3.5" />
          <span>Viewing as {user?.email}</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void stopImpersonating()}
            disabled={stopImpersonatingPending}
            className="h-6 px-2 text-xs text-warning hover:text-warning hover:bg-warning/10"
          >
            Exit
          </Button>
        </div>
      )}

      {/* Two-factor put off at login ("Skip for now"). Stays until enrolled. */}
      {mfaDeferred && !impersonation.isImpersonating && (
        <div className="flex min-h-9 flex-wrap items-center justify-center gap-x-2 gap-y-1 border-b border-warning/20 bg-warning/10 px-4 py-1.5 text-xs font-medium text-warning">
          <ShieldAlert className="h-3.5 w-3.5" />
          <span>Two-factor authentication isn't set up on your account.</span>
          <Link
            to="/auth/mfa/enroll"
            className="underline underline-offset-2 hover:no-underline"
          >
            Set it up
          </Link>
        </div>
      )}

      {/* No background: the bar sits above the scroll area, not over it, so it
          should read as the ambient canvas closed by a single hairline. */}
      <header
        data-app-chrome
        className="flex h-16 items-center justify-between gap-4 border-b border-border/60 px-4"
      >
        {/* The sidebar's way in below `md`, where the rail is a drawer. Not
            rendered as a flex item from `md` up (display: none), so the
            header's spacing there is what it was. */}
        {nav && (
          <Button
            ref={nav.buttonRef}
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => nav.setOpen(true)}
            aria-label="Open navigation"
            aria-haspopup="dialog"
            aria-expanded={nav.open}
            aria-controls={nav.id}
            className="h-9 w-9 shrink-0 md:hidden"
          >
            <Menu className="h-5 w-5" aria-hidden />
          </Button>
        )}

        {/* Left side - Search. Below `md` its auto right margin keeps it
            beside the menu button rather than centred between the ends. */}
        <div className="flex-1 max-w-md max-md:mr-auto">
          <button
            type="button"
            onClick={() =>
              document.dispatchEvent(
                new KeyboardEvent("keydown", { key: "k", metaKey: true }),
              )
            }
            className="relative flex h-9 w-full items-center rounded-md border border-border bg-muted/40 px-3 text-sm text-muted-foreground transition-colors duration-200 hover:bg-accent hover:text-foreground"
          >
            <Search className="mr-2 h-4 w-4" />
            <span>Search...</span>
            <kbd className="ml-auto hidden h-5 select-none items-center gap-1 rounded border border-border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground sm:flex">
              <span className="text-xs">⌘</span>K
            </kbd>
          </button>
        </div>

        {/* Right side - Actions */}
        <div className="flex items-center gap-1">
          {/* No global date range or Export: the pages that filter by date own
              their range, and only a page's toolbar knows what to export. */}
          <NotificationBell />

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="flex items-center gap-2 pl-2 pr-1.5 h-9 hover:bg-accent"
              >
                <UserAvatar
                  seed={user?.email ?? ""}
                  src={user?.avatar}
                  name={`${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim()}
                  size={28}
                />
                <div className="hidden lg:flex flex-col items-start">
                  <span className="text-xs font-medium leading-none">
                    {user?.firstName} {user?.lastName}
                  </span>
                </div>
                <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col gap-1.5">
                  <p className="text-sm font-semibold">
                    {user?.firstName} {user?.lastName}
                  </p>
                  {/* Wrapped, not truncated, as in the sidebar: a cut email
                      loses its domain. */}
                  <p className="break-words text-xs text-muted-foreground">
                    {user?.email}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge
                      variant="secondary"
                      className="text-[10px] h-5 font-normal"
                    >
                      {labelOf(user?.role) || "User"}
                    </Badge>
                    {user?.companyName && (
                      <Badge
                        variant="outline"
                        className="text-[10px] h-5 font-normal"
                      >
                        {user.companyName}
                      </Badge>
                    )}
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild className="cursor-pointer">
                <Link to="/settings/profile">
                  <User className="mr-2 h-4 w-4" />
                  Profile
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild className="cursor-pointer">
                <Link to="/settings">
                  <Settings className="mr-2 h-4 w-4" />
                  Settings
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleLogout}
                className="text-destructive cursor-pointer focus:text-destructive focus:bg-destructive/10"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
    </div>
  );
}
